import { type RouteLocale } from '@/hooks/useLocale'
import { PTG } from '@/lib/ads/ads-config/homepage/homepageAdSlots'
import { createHostnameLabel } from '@/lib/ads/components/gpt/configGpt'
import { createHashedDeviceIdLabel, getGptDeviceType } from '@/lib/ads/components/gpt/deviceGpt'
import { GPT_KEYS, GPT_VALUES } from '@/lib/ads/constants/gpt'
import { type GptTargeting } from '@/lib/ads/types/adConfig'
import { type LocationPath } from '@/types/location'

/**
 * Formatting logic ported from appendLocationsToSlot in BOX
 * 1. convert to lowercase
 * 2. remove whitespace, periods, single quotes, and parentheses
 * 3. replace slashes (/) with hyphens (-)
 * @param {string} locationName , eg Toronto (GTA) or Banff / Canmore
 * @returns {string}, eg torontogta or banff-canmore
 */
const processLocationName = (locationName: string) => {
  return locationName
    .toLowerCase()
    .replace(/[\s.'()]/g, '')
    .replace(/\//g, '-')
}

/**
 * The locationPaths array contains the location hierarchy,
 * i.e. country -> province/region -> city -> sub-city
 * it will always contain at least the country and province/region unless the locationId is 0
 * @param {number} locationId
 * @param {LocationPath[]}locationPaths
 * @returns {GptTargeting[]} eg [{ key: 'lc', value: '1700234' }, { key: 'province', value: 'ontario' }]
 */
const generateLocationValues = (locationId: number, locationPaths: LocationPath[]) => {
  const locationTargetingValues: GptTargeting[] = locationPaths.reduce(
    (acc, currLocationPath, index) => {
      if (index === 0) return acc // we don't need the country

      // get province/region, eg Onrario
      if (index === 1) {
        const processedLocationName = processLocationName(currLocationPath.name?.en_CA || '')

        return [
          ...acc,
          {
            key: GPT_KEYS.PROVINCE_TARGETING_LABEL,
            value: processedLocationName,
          },
        ]
      }

      // get city name if it exists, eg Toronto (GTA)
      if (index === 2) {
        const processedLocationName = processLocationName(currLocationPath.name?.en_CA || '')

        return [
          ...acc,
          {
            key: GPT_KEYS.CITY_TARGETING_LABEL,
            value: processedLocationName,
          },
        ]
      }

      // get sub-city name if it exists, eg City of Toronto
      if (index === 3) {
        const processedLocationName = processLocationName(currLocationPath.name?.en_CA || '')

        return [
          ...acc,
          {
            key: GPT_KEYS.SUB_CITY_TARGETING_LABEL,
            value: processedLocationName,
          },
        ]
      }

      return acc
    },
    [{ key: GPT_KEYS.LOCATION_ID_LABEL, value: locationId.toString() }]
  )

  return locationTargetingValues
}

export const generateGptTargetingValues = (
  isSignedIn: 'yes' | 'no',
  routeLocale: RouteLocale,
  locationId: number,
  locationPaths: LocationPath[] | undefined,
  hasUserOptedOutTargeting: boolean
) => {
  const isTestMode = process.env.NEXT_PUBLIC_DISPLAY_ADS_TEST_MODE === 'true'

  const targetingValuesWithoutLocation: GptTargeting[] = [
    { key: GPT_KEYS.PAGE_TYPE_LABEL_KJ, value: 'homepage' },
    { key: GPT_KEYS.PAGE_TYPE_LABEL, value: 'Homepage' },
    { key: GPT_KEYS.HOSTNAME_LABEL, value: createHostnameLabel().value },
    { key: GPT_KEYS.PTG_LABEL, value: PTG },
    { key: GPT_KEYS.HASHED_DEVICE_ID_LABEL, value: createHashedDeviceIdLabel().value },
    { key: GPT_KEYS.DEVICE_LABEL, value: getGptDeviceType() },
    { key: GPT_KEYS.USER_LOGGED_IN_LABEL_KJ, value: isSignedIn },
    { key: GPT_KEYS.USER_LOGGED_IN_LABEL, value: isSignedIn },
    { key: GPT_KEYS.LANGUAGE_LABEL, value: routeLocale },
    { key: GPT_KEYS.HEADER_BIDDING_LABEL, value: 'yes' },
    { key: GPT_KEYS.PREBID_VERSION_LABEL, value: GPT_VALUES.PREBID_VERSION },
    {
      key: GPT_KEYS.WEBAPPNAME,
      value: GPT_VALUES.WEBAPPNAME,
    },
    ...(isTestMode ? [{ key: GPT_KEYS.TESTING, value: GPT_VALUES.TESTING }] : []),
    ...(hasUserOptedOutTargeting
      ? [{ key: GPT_KEYS.OPT_OUT, value: GPT_VALUES.OPT_OUT_TRUE }]
      : [{ key: GPT_KEYS.OPT_OUT, value: GPT_VALUES.OPT_OUT_FALSE }]),
  ]

  if (!locationId || !locationPaths) {
    // return with default location (i.e. Canada)
    const gptTargetingValues = [
      ...targetingValuesWithoutLocation,
      { key: GPT_KEYS.LOCATION_ID_LABEL, value: '0' },
    ]
    return gptTargetingValues
  }

  const locationTargetingValues = generateLocationValues(locationId, locationPaths)

  const gptTargetingValues = [...targetingValuesWithoutLocation, ...locationTargetingValues]

  return gptTargetingValues
}
